import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Check, ArrowRight, Phone, Heart, Crown, Users, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ConciergeChat from "@/components/site/ConciergeChat";
import api from "@/lib/api";

const HERO_IMG =
    "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2400";

const PACKAGES = [
    {
        name: "The Intimate",
        headline: "Bride + Maid of Honor",
        price: "Starting $350",
        includes: ["Bridal nails (gel or acrylic)", "Maid of Honor nails", "AI 3D nail art", "On-site in your suite"],
        headcount: "2 people",
    },
    {
        name: "The Party",
        headline: "Full bridal party",
        price: "Starting $850",
        featured: true,
        includes: ["Bridal glow-up (nails + spray tan)", "Up to 6 bridesmaids nails", "AI 3D nail art each", "Coordinated with your timeline"],
        headcount: "Up to 7 people",
    },
    {
        name: "The Full Glow",
        headline: "Nails + Tans + Makeup",
        price: "Custom quote",
        includes: ["Bridal nails, spray tan, makeup", "Party services (nails/tans/makeup)", "Trial-run bridal makeup", "Multi-artist team on-site"],
        headcount: "Any size",
    },
];

export default function Weddings() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        party_size: "",
        location: "",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = "Wedding Party Beauty Services — 24K Glow On The Go";
        return () => { document.title = prevTitle; };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.event_date) {
            toast.error("Please fill in name, email, and event date");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/wedding-inquiry", form);
            toast.success("Inquiry received — we'll reach out within 24 hours ✨");
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send — try texting (904) 672-9966");
        }
        setSubmitting(false);
    };

    return (
        <div data-testid="weddings-page" className="bg-ink text-white relative overflow-hidden bg-grain min-h-screen">
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-center pt-40 pb-16">
                <div className="absolute inset-0 z-0">
                    <img src={HERO_IMG} alt="Bridal party beauty" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 w-full">
                    <p className="text-xs tracking-[0.4em] uppercase text-gold mb-6 flex items-center gap-2">
                        <Heart className="w-4 h-4" /> Wedding parties & events
                    </p>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl">
                        Your bridal party&rsquo;s
                        <br />
                        <span className="italic text-rose">glow team,</span>
                        <br />
                        <span className="gold-underline">delivered.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-white/80 leading-relaxed italic font-serif">
                        One booking. One team of pros. <span className="text-gold not-italic font-medium">On-site in your suite.</span>
                    </p>
                    <p className="mt-8 max-w-2xl text-lg text-white/75 leading-relaxed">
                        Nails, spray tans, makeup, and hair styled at your venue or hotel suite — so the entire party is camera-ready without one salon trip.
                        AI 3D nail art included in every package. Serving Jacksonville, St. Augustine, Orange Park, and North Florida.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <a
                            href="#inquiry"
                            data-testid="wedding-hero-cta"
                            className="px-6 py-3 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition inline-flex items-center gap-2"
                        >
                            Request a quote <ArrowRight className="w-4 h-4" />
                        </a>
                        <a
                            href="tel:+19046729966"
                            data-testid="wedding-hero-call"
                            className="px-6 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition inline-flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> (904) 672-9966
                        </a>
                    </div>
                </div>
            </section>

            {/* PACKAGES */}
            <section className="relative py-24 border-t border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Signature packages</p>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">Three ways to glow together.</h2>
                    <p className="mt-4 text-white/60 max-w-2xl">All packages include on-site setup, sanitized tools, patent-pending AI 3D nail art, and coordinated timing with your ceremony.</p>

                    <div className="mt-14 grid md:grid-cols-3 gap-6">
                        {PACKAGES.map((pkg) => (
                            <div
                                key={pkg.name}
                                data-testid={`wedding-pkg-${pkg.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`relative rounded-3xl p-8 border ${
                                    pkg.featured
                                        ? "border-gold/50 bg-gradient-to-br from-gold/8 via-rose/5 to-transparent"
                                        : "border-white/10 bg-surface"
                                }`}
                            >
                                {pkg.featured && (
                                    <div className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-gold text-ink text-[10px] tracking-[0.25em] uppercase">Most booked</div>
                                )}
                                <div className="flex items-center gap-2 text-rose">
                                    {pkg.featured ? <Crown className="w-4 h-4 text-gold" /> : <Sparkles className="w-4 h-4" />}
                                    <p className="text-xs tracking-[0.3em] uppercase">{pkg.name}</p>
                                </div>
                                <p className="font-serif text-3xl mt-3">{pkg.headline}</p>
                                <p className={`font-serif text-4xl mt-4 ${pkg.featured ? "text-gold" : "text-rose"}`}>{pkg.price}</p>
                                <p className="text-xs tracking-widest uppercase text-white/45 mt-1 flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {pkg.headcount}
                                </p>
                                <ul className="mt-6 space-y-2 text-sm text-white/80">
                                    {pkg.includes.map((it) => (
                                        <li key={it} className="flex items-start gap-2">
                                            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${pkg.featured ? "text-gold" : "text-rose"}`} /> {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="relative py-24 border-t border-hairline bg-surface/40">
                <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="text-xs tracking-[0.3em] uppercase text-gold">How it works</p>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">Zero salon runs. Zero stress.</h2>
                    <div className="mt-12 grid md:grid-cols-4 gap-4">
                        {[
                            { n: "1", t: "Send inquiry", b: "Tell us the date, party size, and where you're getting ready." },
                            { n: "2", t: "Get your quote", b: "We reply within 24 hours with a custom timeline and price." },
                            { n: "3", t: "Lock the date", b: "Small deposit holds your spot. Balance due on wedding day." },
                            { n: "4", t: "We show up", b: "Our team arrives, sets up, and works alongside your timeline." },
                        ].map((s) => (
                            <div key={s.n} className="rounded-2xl border border-white/10 bg-surface p-6">
                                <p className="font-serif text-3xl text-rose">0{s.n}</p>
                                <p className="font-serif text-xl mt-3">{s.t}</p>
                                <p className="text-sm text-white/60 mt-1">{s.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* INQUIRY FORM */}
            <section id="inquiry" className="relative py-24 border-t border-hairline">
                <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Request a quote</p>
                    <h2 className="font-serif text-4xl sm:text-5xl mt-3">Tell us about your day.</h2>
                    <p className="mt-4 text-white/60">We&rsquo;ll get back to you within 24 hours with a custom quote and available dates.</p>

                    {sent ? (
                        <div data-testid="wedding-inquiry-success" className="mt-10 rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/8 via-rose/5 to-transparent p-10 text-center">
                            <Heart className="w-10 h-10 text-gold mx-auto" />
                            <h3 className="font-serif text-3xl mt-4">Inquiry received</h3>
                            <p className="text-white/70 mt-3">We&rsquo;ll reach out within 24 hours with your quote and availability. Congratulations! ✨</p>
                            <a
                                href="tel:+19046729966"
                                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition"
                            >
                                <Phone className="w-4 h-4" /> Or call (904) 672-9966
                            </a>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} data-testid="wedding-inquiry-form" className="mt-10 rounded-3xl border border-white/10 bg-surface p-8 space-y-5">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Your name *</label>
                                    <input
                                        data-testid="wedding-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        placeholder="Bride, planner, or MOH"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Event date *</label>
                                    <input
                                        data-testid="wedding-input-date"
                                        type="date"
                                        value={form.event_date}
                                        onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Email *</label>
                                    <input
                                        data-testid="wedding-input-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Phone</label>
                                    <input
                                        data-testid="wedding-input-phone"
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        placeholder="(904) 555-1234"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Party size</label>
                                    <input
                                        data-testid="wedding-input-party-size"
                                        value={form.party_size}
                                        onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        placeholder="e.g. Bride + 5 bridesmaids"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-widest uppercase text-white/50">Venue / location</label>
                                    <input
                                        data-testid="wedding-input-location"
                                        value={form.location}
                                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                                        className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                        placeholder="Hotel, venue, or address"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-white/50">What are you dreaming of?</label>
                                <textarea
                                    data-testid="wedding-input-notes"
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={4}
                                    className="mt-2 w-full px-4 py-3 rounded-xl bg-ink/50 border border-white/10 focus:border-rose focus:outline-none transition"
                                    placeholder="Nail styles, makeup looks, timing, anything else…"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="wedding-inquiry-submit"
                                disabled={submitting}
                                className="mt-4 w-full md:w-auto px-8 py-4 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {submitting ? "Sending..." : "Send inquiry"}
                            </button>
                        </form>
                    )}
                </div>
            </section>

            <Footer />
            <ConciergeChat />
        </div>
    );
}
